import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, LogOut, User as UserIcon } from "lucide-react";
import api from "@/lib/api";

export default function CollaboratorSessions() {
  const { t } = useTranslation();
  const [collabs, setCollabs] = useState([]);
  const [selected, setSelected] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/collaborators").then(({ data }) => {
      if (!mounted) return;
      const items = data.items || [];
      setCollabs(items);
      // Only auto-select on the very first load, when no collaborator is chosen.
      setSelected((prev) => prev || (items[0]?.id ?? ""));
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/collaborators/${selected}/sessions`);
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  }, [selected]);
  useEffect(() => { load(); }, [load]);

  const forceLogout = async () => {
    if (!window.confirm(t("collab.force_logout_confirm", "Revoke all active sessions for this collaborator?"))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/collaborators/${selected}/force-logout`);
      toast.success(t("collab.force_logout_done", "Revoked {{n}} sessions", { n: data.revoked }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="collab-sessions-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Collaborators</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("collab.sessions_title", "Collaborator sessions")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("collab.sessions_hint", "Active devices for each collaborator. Force logout at any time.")}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <UserIcon className="w-4 h-4 text-slate-400" />
        <select data-testid="collab-sessions-select" value={selected} onChange={(e) => setSelected(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm">
          {collabs.map((c) => (<option key={c.id} value={c.id}>{`${c.name} (${c.username})`}</option>))}
        </select>
        <button data-testid="collab-force-logout" onClick={forceLogout} disabled={!selected || busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} {t("collab.force_logout", "Force logout")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2">IP</th>
                <th className="px-4 py-2">Device</th>
                <th className="px-4 py-2">Last seen</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">{t("collab.sessions_empty", "No sessions found.")}</td></tr>
              ) : rows.map((s) => (
                <tr key={s.id || s.jti} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 text-slate-700 font-mono text-xs">{s.ip || "—"}</td>
                  <td className="px-4 py-2 text-slate-500 text-xs truncate max-w-xs" title={s.user_agent || ""}>{s.user_agent || "—"}</td>
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2"><span className={"text-xs font-semibold px-2 py-0.5 rounded-full border " + (s.revoked_at ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-emerald-50 text-emerald-700 border-emerald-500")}>{s.revoked_at ? "Revoked" : "Active"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
